// Copyright (c) 2022 by Move Closer

import { AnyObject, MappingConfig } from '@movecloser/front-core'

import { logger } from '../../../support'

import {
  AbstractTypeDriver,
  AnyDescription,
  DriverConfig,
  PossibleTypeDriver,
  Related,
  RelatedRecord,
  RelatedType,
  ResolvesDriver
} from '../../shared'

import {
  DescriptionOfLink,
  DescriptionOfNavigation,
  NavigationItem,
  NavigationRawItem,
  NavigationTarget
} from '../contracts'
import { linkAdapterMap } from '../adapters'

/**
 * Driver for the `RelatedService` capable of resolving a single link.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export class LinkDriver extends AbstractTypeDriver<DescriptionOfLink> {
  /**
   * @inheritDoc
   */
  protected adapterMap: MappingConfig = linkAdapterMap

  /**
   * @inheritDoc
   */
  protected dependencies: RelatedType[] = []

  /**
   * @inheritDoc
   */
  protected key: RelatedType = LinkRelatedType
}

export const LinkRelatedType: RelatedType = 'link'

/**
 * Driver for the `RelatedService` capable of resolving a single navigation.
 *
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
export class NavigationDriver extends AbstractTypeDriver<DescriptionOfNavigation, NavigationItem> {
  protected adapterMap: MappingConfig = {}
  protected dependencies: RelatedType[] = [LinkRelatedType]
  protected key: RelatedType = NavigationRelatedType

  /**
   * @inheritDoc
   */
  protected resolveNested (
    unresolved: DescriptionOfNavigation,
    record: RelatedRecord,
    config: DriverConfig,
    driverResolver?: ResolvesDriver
  ): AnyDescription {
    if (typeof driverResolver === 'undefined') {
      throw new Error('[NavigationDriver]: Missing driverResolver.')
    }
    const linkResolver = driverResolver(LinkRelatedType)

    const links = unresolved.links.map((link: NavigationRawItem) => {
      try {
        let target: string | null = null

        if (link.target && Object.prototype.hasOwnProperty.call(link.target, 'value')) {
          const resolved = linkResolver.resolve(`${(link.target as Related).value}`, record, config)
          target = typeof resolved === 'string' ? resolved : resolved.url
        }

        if (link.target && Object.prototype.hasOwnProperty.call(link.target, 'link')) {
          target = (link.target as NavigationTarget).link
        }

        let resolvedSnippet: AnyObject | undefined
        if (link.snippet) {
          const snippetResolver = driverResolver(link.snippet.type)
          try {
            resolvedSnippet = snippetResolver.resolve(`${link.snippet.value}`, record, config)
          } catch (e) {
            logger(e, 'warn')
          }
        }

        return {
          ...link,
          target,
          children: resolveNestedLinks(link.children, linkResolver, record, config).filter(child => !child.exclude),
          snippet: resolvedSnippet
        }
      } catch (e) {
        return {
          ...link,
          exclude: true,
          label: null,
          target: null
        }
      }
    })

    return {
      ...unresolved,
      links
    }
  }
}

/**
 * Resolves nested links in navigation.
 *
 * @param links - list of `NavigationRawItem`
 * @param linkResolver - driver
 * @param record - related records.
 * @param config - related config
 */
function resolveNestedLinks (
  links: NavigationRawItem[],
  linkResolver: PossibleTypeDriver,
  record: RelatedRecord,
  config: AnyObject
): AnyObject[] {
  return links.map((link: NavigationRawItem) => {
    try {
      const { strategy, children, ..._link } = link
      let newTarget: string | null = null

      if (link.target && Object.prototype.hasOwnProperty.call(link.target, 'value')) {
        const resolved = linkResolver.resolve(`${(link.target as Related).value}`, record, config)
        newTarget = typeof resolved === 'string' ? resolved : resolved.url
      }

      if (link.target && Object.prototype.hasOwnProperty.call(link.target, 'link')) {
        newTarget = (link.target as NavigationTarget).link
      }

      return {
        ..._link,
        tabTarget: `_${strategy}`,
        target: newTarget,
        children: resolveNestedLinks(children, linkResolver, record, config)
      }
    } catch (e) {
      return {
        ...link,
        exclude: true
      }
    }
  })
}

export const NavigationRelatedType = 'nav'
